/**
 * What a PDF or SVG export carries, chosen each time (D35). The PDF's pages
 * are views of the design, named by the legend each carries; both formats can
 * add the bills of materials, which the PDF puts together on a page after the
 * views and the SVG draws as tables under the drawing. A choice that would add
 * nothing (no external interfaces to wash, no notes, no set boundary to bill
 * either side of) is listed but cannot be made, with the reason beside it.
 */
import { useEffect, useId, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import type { PdfView, PdfViewId } from "../render/still";
import { BOM_EXPORTS, type BomId } from "./billOfMaterials";

export interface ExportChoice {
  id: string;
  label: string;
  /** Why it cannot be chosen, or null when it can. */
  unavailable: string | null;
  /** Ticked to start with, where it can be chosen. */
  ticked: boolean;
}

export interface ExportChoiceGroup {
  legend: string;
  choices: readonly ExportChoice[];
}

/** The PDF's: each view that can be drawn, ticked; then the bills of materials, left to be asked for. */
export function pdfChoices(
  views: readonly { view: PdfView; unavailable: string | null }[],
  bomUnavailable: string | null,
): ExportChoiceGroup[] {
  return [
    {
      legend: "One A3 page per view, framed alike, each labelled with its view.",
      choices: views.map(({ view, unavailable }) => ({ id: view.id, label: view.legend.label, unavailable, ticked: true })),
    },
    bomGroup("Bills of materials, together on one page after the views.", bomUnavailable),
  ];
}

/** The SVG's: the drawing as the screen has it, and whichever bills of materials are asked for under it. */
export function svgChoices(bomUnavailable: string | null): ExportChoiceGroup[] {
  return [bomGroup("Bills of materials, as tables under the drawing.", bomUnavailable)];
}

function bomGroup(legend: string, unavailable: string | null): ExportChoiceGroup {
  return { legend, choices: BOM_EXPORTS.map((entry) => ({ id: entry.id, label: entry.label, unavailable, ticked: false })) };
}

const BOM_IDS: ReadonlySet<string> = new Set<BomId>(BOM_EXPORTS.map((entry) => entry.id));

/** The PDF views among the choices, and how many pages they print: one each, and one for the bills of materials. */
export function pdfPagesChosen(chosen: ReadonlySet<string>): { pages: Set<PdfViewId>; count: number } {
  const pages = new Set([...chosen].filter((id) => !BOM_IDS.has(id)) as PdfViewId[]);
  return { pages, count: pages.size + ([...chosen].some((id) => BOM_IDS.has(id)) ? 1 : 0) };
}

export function ExportOptionsDialog({
  title,
  groups,
  submit,
  onExport,
  onClose,
}: {
  title: string;
  groups: readonly ExportChoiceGroup[];
  /** The export button's text for what is chosen, and whether it can export it. */
  submit: (chosen: ReadonlySet<string>) => { label: string; disabled: boolean };
  onExport: (chosen: ReadonlySet<string>) => void;
  onClose: () => void;
}) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const [chosen, setChosen] = useState<ReadonlySet<string>>(
    () =>
      new Set(
        groups.flatMap((group) => group.choices).filter((choice) => choice.ticked && choice.unavailable === null).map((choice) => choice.id),
      ),
  );

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    dialogRef.current?.querySelector<HTMLElement>("input:not([disabled]), button.primary:not([disabled])")?.focus();
    return () => previous?.focus?.();
  }, []);

  // Escape closes; Tab stays inside, as in the other dialogs.
  const onKeyDown = (event: ReactKeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key !== "Tab") return;
    const focusable = [
      ...(dialogRef.current?.querySelectorAll<HTMLElement>("button:not([disabled]), input:not([disabled])") ?? []),
    ];
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const toggle = (id: string, on: boolean) =>
    setChosen((current) => {
      const next = new Set(current);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const button = submit(chosen);
  return (
    <div className="dialog-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div
        ref={dialogRef}
        className="dialog card ws-export-options"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={onKeyDown}
      >
        <h3 id={titleId}>{title}</h3>
        {groups.map((group) => (
          <fieldset key={group.legend}>
            <legend className="ws-muted ws-small">{group.legend}</legend>
            {group.choices.map(({ id, label, unavailable }) => (
              <label key={id} className={unavailable ? "ws-export-option ws-muted" : "ws-export-option"}>
                <input
                  type="checkbox"
                  checked={chosen.has(id)}
                  disabled={unavailable !== null}
                  onChange={(event) => toggle(id, event.target.checked)}
                />
                <span>{label}</span>
                {unavailable && <span className="ws-small"> · {unavailable}</span>}
              </label>
            ))}
          </fieldset>
        ))}
        <div className="row dialog-actions">
          <button type="button" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="primary" disabled={button.disabled} onClick={() => onExport(chosen)}>
            {button.label}
          </button>
        </div>
      </div>
    </div>
  );
}
